import clsx from 'clsx';
import { formatCompactCurrency } from '@/lib/format';
import { panelStyle, textGlowStyle } from './CapitalStackBar';

/**
 * Project Sources — block/panel rendering for the new-development Signal
 * variant (replaces the WS3 `<ProjectSourcesTable>`). Mirrors
 * `<CapitalStackBar>`'s Lovable-aligned chunky-panel style: a top row of
 * proportional source panels (Senior Loan purple + each non-zero Incentive
 * amber + Equity blue) over a full-width green Total Sources panel.
 *
 * INCENTIVES SHOW ONLY WHEN THEY EXIST — the caller passes incentive slices
 * only when their amount is finite and > 0 (per the 2026-06-01 request). With
 * no incentives the layout is exactly Senior + Equity + Total (screenshot
 * parity with the pre-WS3 block).
 *
 * DUAL DENOMINATOR (carried from the table it replaces): each top panel's % is
 * `amount / totalSources` (the panels sum to 100%); the bottom Total Sources
 * panel shows the headline `totalSources / TDC` (`totalPercent`), the only place
 * it can exceed 100% when incentives over-fund the stack.
 */
export type ProjectSourceTier = 'senior' | 'incentive' | 'equity';

export interface ProjectSourceSlice {
  label: string;
  amount: number;
  tier: ProjectSourceTier;
}

export interface ProjectSourcesBarProps {
  /** Senior + non-zero incentives + equity, in render order. */
  slices: ProjectSourceSlice[];
  /** Sum of the source slices (the bottom panel's amount). */
  total: number;
  /** Headline ratio `totalSources / TDC` — can exceed 100%; NaN → no %. */
  totalPercent: number;
  title?: string;
  totalLabel?: string;
  className?: string;
}

// Per-tier hex — mirrors CapitalStackBar's TIER_HEX, plus `incentive` (amber).
const SOURCE_HEX: Record<ProjectSourceTier | 'total', string> = {
  senior: '#7860a8', // @design-token resolves --color-purple
  incentive: '#d4960f', // @design-token resolves --color-warn
  equity: '#3a78b8', // @design-token resolves --color-blue
  total: '#00c853', // @design-token resolves --color-accent
};

export function ProjectSourcesBar({
  slices,
  total,
  totalPercent,
  title = 'Project Sources',
  totalLabel = 'Total Sources',
  className,
}: ProjectSourcesBarProps) {
  const rendered = slices.filter(
    (s) => Number.isFinite(s.amount) && s.amount > 0
  );
  const sliceSum = rendered.reduce((sum, s) => sum + s.amount, 0);
  const isEmpty = !(total > 0) || rendered.length === 0 || sliceSum <= 0;

  if (isEmpty) {
    return (
      <div
        data-testid="project-sources-bar"
        data-total={Number.isFinite(total) ? total : 0}
        data-empty="true"
        className={clsx('flex flex-col gap-1.5 px-3 py-2', className)}
      >
        <span className="text-[9px] font-mono text-text-dim uppercase tracking-[0.6px]">
          No project sources data
        </span>
      </div>
    );
  }

  const accentHex = SOURCE_HEX.total;
  const headlinePct = Number.isFinite(totalPercent)
    ? `${(totalPercent * 100).toFixed(1)}%`
    : '—';

  return (
    <div
      data-testid="project-sources-bar"
      data-total={total}
      data-slice-count={rendered.length}
      data-empty="false"
      className={clsx('flex flex-col gap-[2px] px-3 pt-2 pb-2', className)}
    >
      <div className="flex items-center gap-2.5 mb-1">
        <span
          aria-hidden="true"
          className="w-1 h-1 rounded-full bg-accent shrink-0"
          style={{ boxShadow: '0 0 4px rgba(0,200,83,0.6)' }}
        />
        <h3 className="text-[10px] font-mono uppercase tracking-[2px] text-accent font-bold">
          {title}
        </h3>
        <span className="flex-1 h-px bg-panel-border" />
      </div>

      {/* Top row — one proportional panel per source (senior + incentives + equity). */}
      <div className="flex h-[54px] gap-[2px]">
        {rendered.map((s, i) => {
          const hex = SOURCE_HEX[s.tier];
          const pct = sliceSum > 0 ? (s.amount / sliceSum) * 100 : 0;
          return (
            <div
              key={`${s.tier}-${s.label}-${i}`}
              data-testid={`project-source-panel-${s.tier}`}
              className="flex flex-col justify-between overflow-hidden"
              style={{
                width: `${pct}%`,
                padding: '6px 9px',
                ...panelStyle(hex),
              }}
            >
              <div className="flex items-baseline justify-between gap-2">
                <span
                  className="font-mono text-[9px] font-bold tracking-[1.5px] uppercase truncate"
                  style={textGlowStyle(hex, '66')}
                >
                  {s.label}
                </span>
                <span
                  className="font-mono text-[15px] font-bold tabular-nums tracking-[-0.3px]"
                  style={textGlowStyle(hex, '55')}
                >
                  {pct.toFixed(1)}%
                </span>
              </div>
              <div className="flex items-baseline justify-end gap-2 min-w-0">
                <span
                  className="font-mono text-[10.5px] font-semibold tabular-nums"
                  style={textGlowStyle(hex, '55')}
                >
                  {formatCompactCurrency(s.amount)}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Bottom panel — Total Sources full-width (headline % = sources / TDC). */}
      <div
        className="flex flex-col justify-between"
        style={{
          height: '54px',
          padding: '6px 10px',
          ...panelStyle(accentHex),
        }}
      >
        <div className="flex items-baseline justify-between gap-2">
          <span
            className="font-mono text-[9px] font-bold tracking-[1.5px] uppercase"
            style={textGlowStyle(accentHex, '66')}
          >
            {totalLabel.toUpperCase()}
          </span>
          <span
            className="font-mono text-[15px] font-bold tabular-nums tracking-[-0.3px]"
            style={textGlowStyle(accentHex, '55')}
          >
            {headlinePct}
          </span>
        </div>
        <div className="flex items-baseline justify-between gap-2">
          <span
            className="font-mono text-[8px] tracking-[0.4px]"
            style={{ color: 'rgba(255,255,255,0.75)' }}
          >
            All-in capital sources
          </span>
          <span
            className="font-mono text-[12px] font-bold tabular-nums"
            style={textGlowStyle(accentHex, '55')}
          >
            {formatCompactCurrency(total)}
          </span>
        </div>
      </div>
    </div>
  );
}
